import { FC, useEffect, useMemo, useState } from "react";

import {
  Box,
  Card,
  ErrorWithRetry,
  HStack,
  Skeleton,
  VStack,
} from "@/components/ui";
import { useLocalization, useRunContext, useStudyContext } from "@/hooks";
import API from "@/hooks/api";
import {
  DemographicFiltersMap,
  QuestionResults as QuestionResultsType,
  Summary as SummaryType,
} from "@/types/resources";
import LinearScaleQuestionResults from "./LinearScaleQuestionResults";
import MultiSelectMatrixQuestionResults from "./MultiSelectMatrixQuestionResults";
import MultipleChoiceQuestionResults from "./MultipleChoiceQuestionResults";
import NPSResults from "./NPSResults";
import OpenEndedQuestionResults from "./OpenEndedQuestionResults";
import PricingResult from "./PricingResult";
import RankingQuestionResults from "./RankingQuestionResults";
import RatingScaleQuestionResults from "./RatingScaleQuestionResults";
import SingleChoiceQuestionResults from "./SingleChoiceQuestionResults";
import SingleSelectMatrixQuestionResults from "./SingleSelectMatrixQuestionResults";
import Summary, { SummaryEmpty, SummarySkeleton } from "./Summary";
import VerbatimsPreview from "./VerbatimsPreview";

/* ── Types ─────────────────────────────────────────────── */

interface QuestionResultsProps {
  filters?: DemographicFiltersMap;
  questionId: string;
  result: QuestionResultsType;
  runId: string | null;
  /** Rotation cell to narrow answers to (use-case sections). The parent
      (e.g. the `RunResults` header) owns the labeled selector since
      labels come from the section's stimuli; here we only ever receive
      the chosen cell index. `null`/`undefined` means "all cells". */
  selectedCell?: number | null;
  /** Group selection is owned by the parent (e.g. the `RunResults`
      header dropdown). `"all"` includes every group; a group id narrows
      to that group. Defaults to the only group when there's just one. */
  selectedGroupIndex?: "all" | string;
}

/* ── Skeleton ───────────────────────────────────────────── */

export const QuestionResultsSkeleton: FC = () => (
  <VStack className="gap-6">
    <Box
      className="rounded-2xl p-6"
      style={{ backgroundColor: "hsl(222, 24%, 12%)" }}
    >
      <VStack className="gap-3">
        <HStack className="items-center justify-between">
          <Skeleton className="h-3 w-24 rounded opacity-30" />
          <Skeleton className="h-3.5 w-3.5 rounded opacity-20" />
        </HStack>
        <Skeleton className="h-4 w-full rounded opacity-20" />
        <Skeleton className="h-4 w-5/6 rounded opacity-20" />
        <Skeleton className="h-4 w-4/6 rounded opacity-20" />
      </VStack>
    </Box>
    <Card className="border border-outline-200 rounded-2xl p-6">
      <VStack className="gap-5">
        <HStack className="items-center justify-between">
          <Skeleton className="h-4 w-48 rounded" />
          <Skeleton className="h-4 w-4 rounded" />
        </HStack>
        <Skeleton className="h-64 w-full rounded" />
      </VStack>
    </Card>
    <VStack className="gap-5">
      <Skeleton className="h-4 w-44 rounded" />
      <Box className="flex-row flex-wrap" style={{ gap: 16 }}>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            style={{
              flexBasis: "calc(25% - 12px)",
              flexGrow: 1,
              minWidth: 220,
            }}
          >
            <Card className="border border-outline-200 rounded-2xl p-5 h-full">
              <VStack className="gap-4 h-full justify-between">
                <HStack className="items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                  <VStack className="flex-1 gap-1.5">
                    <Skeleton className="h-3.5 w-24 rounded" />
                    <Skeleton className="h-3 w-32 rounded" />
                  </VStack>
                </HStack>
                <VStack className="gap-1.5">
                  <Skeleton className="h-3 w-full rounded" />
                  <Skeleton className="h-3 w-5/6 rounded" />
                  <Skeleton className="h-3 w-2/3 rounded" />
                </VStack>
              </VStack>
            </Card>
          </Box>
        ))}
      </Box>
    </VStack>
  </VStack>
);

/* ── Component ──────────────────────────────────────────── */

const QuestionResults: FC<QuestionResultsProps> = ({
  filters,
  questionId,
  result,
  runId,
  selectedCell,
  selectedGroupIndex: externalGroupIndex,
}) => {
  const { i18n } = useLocalization();
  const { studyId, studyType } = useStudyContext();
  const { runId: contextRunId } = useRunContext();
  const effectiveRunId = runId ?? contextRunId ?? "";
  const { groups, question } = result;

  // Lazy summary generation — use studies or adhoc hook depending on context
  const {
    generateSummary: generateStudySummary,
    loading: generatingStudySummary,
  } = API.Studies.useGenerateRunSummary(studyId ?? "", effectiveRunId);
  const {
    generateSummary: generateAdhocSummary,
    loading: generatingAdhocSummary,
  } = API.Adhoc.useGenerateRunSummary(questionId, effectiveRunId);
  const generateSummary = studyId ? generateStudySummary : generateAdhocSummary;
  const generatingSummary = studyId
    ? generatingStudySummary
    : generatingAdhocSummary;

  const defaultGroupIndex = useMemo<"all" | string>(() => {
    if (groups.length === 1) return groups[0].group.id || "0";
    return "all";
  }, [groups]);

  const selectedGroupIndex = externalGroupIndex ?? defaultGroupIndex;

  const isQuantitativeNonOpenEnded =
    studyType === "quantitative" && question.type !== "open_ended";

  const [generatedSummary, setGeneratedSummary] = useState<SummaryType | null>(
    null
  );
  const [summaryError, setSummaryError] = useState(false);

  const rawSummary = useMemo(() => {
    // Monadic-rotated sections carry a per-stimulus summary keyed by cell; use
    // it when a cell is selected, falling back to the cell-agnostic summary
    // (e.g. old runs that predate per-cell summaries) so lazy generation can
    // kick in when neither exists.
    if (selectedGroupIndex === "all") {
      if (selectedCell != null) {
        return result.cell_summaries?.[selectedCell] ?? null;
      }
      return result.summary ?? null;
    }
    const group = groups.find(
      (g, idx) => (g.group.id ?? String(idx)) === selectedGroupIndex
    );
    if (selectedCell != null) {
      return group?.cell_summaries?.[selectedCell] ?? null;
    }
    return group?.summary ?? null;
  }, [groups, result, selectedGroupIndex, selectedCell]);

  const activeSummary: SummaryType | null = rawSummary ?? generatedSummary;

  const filteredResult = useMemo<QuestionResultsType>(() => {
    // 1. Narrow to the selected group.
    let base =
      selectedGroupIndex === "all"
        ? result
        : {
            ...result,
            groups: groups.filter(
              (group, idx) =>
                (group.group.id ?? String(idx)) === selectedGroupIndex
            ),
          };
    // 2. Narrow each group's answers to the selected rotation cell.
    if (selectedCell != null) {
      base = {
        ...base,
        groups: base.groups.map((g) => ({
          ...g,
          answers: g.answers.filter((a) => a.cell === selectedCell),
        })),
      };
    }
    // 3. Inject the active (group-scoped) summary.
    if (!activeSummary) return base;
    if (selectedGroupIndex === "all") {
      return { ...base, summary: activeSummary };
    }
    return {
      ...base,
      groups: base.groups.map((g) => ({ ...g, summary: activeSummary })),
    };
  }, [groups, result, selectedGroupIndex, selectedCell, activeSummary]);

  const allAnswers = useMemo(
    () => filteredResult.groups.flatMap((g) => g.answers),
    [filteredResult.groups]
  );

  // No respondents → nothing to summarize: skip generation entirely and let
  // the summary/verbatims sections render their own "No data available".
  const hasRespondents = allAnswers.length > 0;

  const renderChart = () => {
    switch (question.type) {
      case "open_ended":
        return <OpenEndedQuestionResults result={filteredResult} />;
      case "single_choice":
        return (
          <SingleChoiceQuestionResults
            result={filteredResult}
            selectedGroupIndex={selectedGroupIndex}
          />
        );
      case "multiple_choice":
        return (
          <MultipleChoiceQuestionResults
            result={filteredResult}
            selectedGroupIndex={selectedGroupIndex}
          />
        );
      case "rating_scale":
        return (
          <RatingScaleQuestionResults
            result={filteredResult}
            selectedGroupIndex={selectedGroupIndex}
          />
        );
      case "linear_scale":
        return (
          <LinearScaleQuestionResults
            result={filteredResult}
            selectedGroupIndex={selectedGroupIndex}
          />
        );
      case "nps":
        return <NPSResults result={filteredResult} />;
      case "pricing":
        return <PricingResult result={filteredResult} />;
      case "ranking":
        return <RankingQuestionResults result={filteredResult} />;
      case "single_select_matrix":
        return <SingleSelectMatrixQuestionResults result={filteredResult} />;
      case "multi_select_matrix":
        return <MultiSelectMatrixQuestionResults result={filteredResult} />;
      default:
        return null;
    }
  };

  const triggerGeneration = async () => {
    const groupId =
      selectedGroupIndex === "all"
        ? null
        : (filteredResult.groups[0]?.group.id ?? null);
    setSummaryError(false);
    setGeneratedSummary(null);
    const serializedFilters = filters
      ? Object.fromEntries(
          Object.entries(filters)
            .filter(([, v]) => v && v.length > 0)
            .map(([k, v]) => [k, v!.join(",")])
        )
      : undefined;
    const summary = await generateSummary({
      cell: selectedCell ?? null,
      filters: serializedFilters,
      group_id: groupId,
      question_id: result.question.id,
    });
    if (summary) {
      setGeneratedSummary(summary);
    } else {
      setSummaryError(true);
    }
  };

  const needsSummary =
    !isQuantitativeNonOpenEnded &&
    !!effectiveRunId &&
    rawSummary === null &&
    hasRespondents;

  // Clear stale summary when the group or the selected rotation cell changes.
  // Done during render (not in an effect) to avoid a cascading re-render.
  const [prevSelectedGroupIndex, setPrevSelectedGroupIndex] =
    useState(selectedGroupIndex);
  const [prevSelectedCell, setPrevSelectedCell] = useState(selectedCell);
  if (
    prevSelectedGroupIndex !== selectedGroupIndex ||
    prevSelectedCell !== selectedCell
  ) {
    setPrevSelectedGroupIndex(selectedGroupIndex);
    setPrevSelectedCell(selectedCell);
    setGeneratedSummary(null);
    setSummaryError(false);
  }

  // Generate the summary whenever one is needed (it is always visible). The
  // trigger is deferred to a timer so its state updates don't run synchronously
  // inside the effect.
  useEffect(() => {
    if (!needsSummary || generatingSummary || summaryError) return;
    const timeout = setTimeout(() => {
      void triggerGeneration();
    }, 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSummary, selectedGroupIndex, selectedCell]);

  return (
    <VStack className="gap-6">
      {!isQuantitativeNonOpenEnded &&
        (activeSummary ? (
          <Summary summary={activeSummary} />
        ) : !hasRespondents ? (
          <SummaryEmpty />
        ) : summaryError ? (
          <ErrorWithRetry
            error={i18n.t("misc.failedToGenerateSummary")}
            onRetry={triggerGeneration}
            retrying={generatingSummary}
          />
        ) : (
          <SummarySkeleton generating={generatingSummary} />
        ))}
      {generatingSummary && question.type === "open_ended" ? (
        <Card className="border border-outline-200 rounded-2xl p-6">
          <VStack className="gap-4">
            <Skeleton className="h-4 w-48 rounded" />
            <Skeleton className="h-52 w-full rounded-xl" />
          </VStack>
        </Card>
      ) : (
        renderChart()
      )}
      {!isQuantitativeNonOpenEnded && (
        <VerbatimsPreview
          answers={allAnswers}
          questionId={questionId}
          runId={runId}
          selectedGroupIndex={selectedGroupIndex}
        />
      )}
    </VStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default QuestionResults;
